import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Brain, Users, Code, Network, Award, Target, Lightbulb, Rocket, Heart, Globe, TrendingUp, BookOpen } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Footer2 } from '../components/Footer2';

const About = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true, threshold: 0.1 });

  const stats = [
    { number: "200+", label: "Active Members", icon: Users },
    { number: "50+", label: "Projects Completed", icon: Code },
    { number: "25+", label: "Industry Partners", icon: Network },
    { number: "10+", label: "Awards Won", icon: Award }
  ];

  const values = [
    {
      icon: Brain,
      title: "Innovation",
      description: "Pushing the boundaries of machine learning research and application through cutting-edge projects and experimentation."
    },
    {
      icon: Users,
      title: "Community",
      description: "Building a supportive network of ML enthusiasts, from beginners to experts, fostering collaboration and growth."
    },
    {
      icon: Target,
      title: "Excellence",
      description: "Striving for excellence in everything we do, from academic research to industry partnerships and community outreach."
    },
    {
      icon: Heart,
      title: "Inclusivity",
      description: "Creating an inclusive environment where everyone, regardless of background, can learn and contribute to ML advancement."
    }
  ];

  const milestones = [
    {
      year: "2020",
      title: "Foundation",
      description: "DMLS was founded by a group of passionate ML students at Dalhousie University.",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=300&fit=crop&crop=center"
    },
    {
      year: "2021",
      title: "First Conference",
      description: "Hosted our inaugural ML conference with 150+ attendees and industry speakers.",
      image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=300&fit=crop&crop=center"
    },
    {
      year: "2022",
      title: "Industry Partnerships",
      description: "Established partnerships with leading tech companies and research institutions.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop&crop=center"
    },
    {
      year: "2023",
      title: "Research Breakthroughs",
      description: "Published groundbreaking research in top ML conferences and journals.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&crop=center"
    },
    {
      year: "2024",
      title: "Global Recognition",
      description: "Received international recognition for our innovative projects and community impact.",
      image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&h=300&fit=crop&crop=center"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-24 pb-16 bg-gradient-to-br from-[#0395fc] to-[#0070c0] relative overflow-hidden">
        {/* Animated background elements */}
        <motion.div
          className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-2xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-24 h-24 bg-yellow-300/20 rounded-full blur-xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full border border-white/30 text-sm font-bold mb-8 backdrop-blur-sm"
            >
              <BookOpen className="size-4" />
              ABOUT DMLS
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 sm:mb-6 leading-tight"
            >
              Empowering the Future
              <br />
              <span className="text-yellow-300">of AI</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-base sm:text-lg lg:text-xl text-white/90 max-w-3xl mx-auto mb-8 sm:mb-12 px-4"
            >
              Dalhousie Machine Learning Society (DMLS) is a vibrant community of innovators, researchers,
              and enthusiasts dedicated to advancing machine learning research and fostering collaboration
              between academia and industry.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                  className="text-center bg-white p-6 rounded-lg shadow-lg border-2 border-black"
                >
                  <Icon className="size-8 mx-auto mb-4 text-[#0395fc]" />
                  <div className="text-2xl lg:text-3xl font-black mb-2 text-[#0395fc]">{stat.number}</div>
                  <div className="text-sm font-bold text-gray-600 uppercase tracking-wider">{stat.label}</div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-[#0395fc]/10 text-[#0395fc] px-4 py-2 rounded-full border-2 border-[#0395fc] text-sm font-bold mb-6">
              <Target className="size-4" />
              OUR MISSION
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-6">
              Driving Innovation in Machine Learning
            </h2>
            <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              At DMLS, we believe that machine learning has the power to transform industries, solve complex problems,
              and create a better future for everyone. Our mission is to democratize access to ML knowledge, foster
              innovation through collaboration, and bridge the gap between academic research and real-world applications.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-black mb-6 text-[#0395fc]">What We Do</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-[#0395fc] p-2 rounded-lg">
                    <Brain className="size-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Research & Development</h4>
                    <p className="text-muted-foreground">Conducting cutting-edge research in machine learning algorithms, applications, and methodologies.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#0395fc] p-2 rounded-lg">
                    <Users className="size-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Community Building</h4>
                    <p className="text-muted-foreground">Creating a supportive network for ML enthusiasts through workshops, seminars, and networking events.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#0395fc] p-2 rounded-lg">
                    <Rocket className="size-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Industry Collaboration</h4>
                    <p className="text-muted-foreground">Partnering with leading tech companies to provide internships, mentorship, and real-world experience.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="w-full"
            >
              <div className="relative">
                {/* Main Card */}
                <div className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 lg:p-8 transform hover:rotate-1 transition-transform duration-300">
                  {/* Large Target Icon */}
                  <div className="flex justify-center mb-6 lg:mb-8">
                    <div className="relative">
                      <div className="bg-[#0395fc] border-2 border-black p-6 lg:p-8 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transform -rotate-3">
                        <Target className="w-20 h-20 lg:w-24 lg:h-24 text-white" />
                      </div>
                      {/* Decorative elements */}
                      <div className="absolute -top-2 -right-2 lg:-top-3 lg:-right-3 w-4 h-4 lg:w-6 lg:h-6 bg-accent border-2 border-black rotate-45"></div>
                      <div className="absolute -bottom-2 -left-2 lg:-bottom-3 lg:-left-3 w-3 h-3 lg:w-4 lg:h-4 bg-secondary border-2 border-black rotate-45"></div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl lg:text-2xl font-black mb-6 lg:mb-8 text-center text-[#0395fc]">Our Impact</h3>

                  {/* Stats Grid */}
                  <div className="space-y-4 lg:space-y-6">
                    <div className="bg-gray-50 border-2 border-black p-4 lg:p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transform rotate-1 text-center">
                      <div className="text-3xl lg:text-4xl font-black text-[#0395fc] mb-2">500+</div>
                      <div className="text-sm lg:text-base font-bold text-gray-600 uppercase tracking-wider">Research Papers</div>
                    </div>
                    <div className="bg-white border-2 border-black p-4 lg:p-6 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transform hover:-rotate-1 transition-transform duration-200 text-center">
                      <div className="text-3xl lg:text-4xl font-black text-[#0395fc] mb-2">50+</div>
                      <div className="text-sm lg:text-base font-bold text-gray-600 uppercase tracking-wider">Industry Partnerships</div>
                    </div>
                    <div className="bg-gray-50 border-2 border-black p-4 lg:p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transform -rotate-1 text-center">
                      <div className="text-3xl lg:text-4xl font-black text-[#0395fc] mb-2">1000+</div>
                      <div className="text-sm lg:text-base font-bold text-gray-600 uppercase tracking-wider">Lives Impacted</div>
                    </div>
                  </div>
                </div>

                {/* Decorative background layers */}
                <div className="absolute -bottom-3 -right-3 lg:-bottom-4 lg:-right-4 w-full h-full bg-[#0395fc]/20 border-2 border-black -z-10 transform rotate-2"></div>
                <div className="absolute -bottom-6 -right-6 lg:-bottom-8 lg:-right-8 w-full h-full bg-[#0395fc]/10 border-2 border-black -z-20 transform -rotate-1"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-[#0395fc]/10 text-[#0395fc] px-4 py-2 rounded-full border-2 border-[#0395fc] text-sm font-bold mb-6">
              <Heart className="size-4" />
              OUR VALUES
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-6">
              What Drives Us
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Our core values guide everything we do, from research initiatives to community outreach programs.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="bg-white p-6 rounded-lg shadow-lg border-2 border-black text-center"
                >
                  <div className="bg-[#0395fc] p-3 rounded-lg w-fit mx-auto mb-4">
                    <Icon className="size-6 text-white" />
                  </div>
                  <h3 className="text-xl font-black mb-3 text-[#0395fc]">{value.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-[#0395fc]/10 text-[#0395fc] px-4 py-2 rounded-full border-2 border-[#0395fc] text-sm font-bold mb-6">
              <TrendingUp className="size-4" />
              OUR JOURNEY
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-6">
              Milestones & Achievements
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              From humble beginnings to becoming a leading force in machine learning education and research.
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-[#0395fc] h-full hidden lg:block"></div>

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className={`flex flex-col lg:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  <div className="flex-1 text-center lg:text-left">
                    <div className="bg-[#0395fc] text-white px-4 py-2 rounded-full font-black text-lg mb-4 inline-block">
                      {milestone.year}
                    </div>
                    <h3 className="text-2xl font-black mb-3 text-[#0395fc]">{milestone.title}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">{milestone.description}</p>
                    
                    {/* Milestone Image */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
                      viewport={{ once: true }}
                      className="relative"
                    >
                      <img
                        src={milestone.image}
                        alt={`${milestone.title} - ${milestone.year}`}
                        className="w-full max-w-md mx-auto lg:mx-0 rounded-lg shadow-lg border-2 border-black transform hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      {/* Decorative corner */}
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-[#0395fc] border-2 border-black rotate-45"></div>
                    </motion.div>
                  </div>

                  {/* Timeline dot */}
                  <div className="w-4 h-4 bg-[#0395fc] rounded-full border-4 border-white shadow-lg hidden lg:block"></div>

                  <div className="flex-1"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24 bg-gradient-to-br from-[#0395fc] to-[#0070c0] text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full border border-white/30 text-sm font-bold mb-8 backdrop-blur-sm">
              <Globe className="size-4" />
              OUR VISION
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-8 leading-tight">
              Shaping the Future of Artificial Intelligence
            </h2>

            <p className="text-lg sm:text-xl leading-relaxed mb-12 text-white/90">
              We envision a world where machine learning is accessible to everyone, where innovation knows no boundaries,
              and where technology serves humanity's greatest challenges. Through education, research, and collaboration,
              we're building the foundation for a brighter, more intelligent future.
            </p>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              className="inline-block bg-white text-[#0395fc] px-8 py-4 font-black border-2 border-white shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] transition-all duration-200"
            >
              Join Our Mission
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer2 />
    </div>
  );
};

export default About;